import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAsyncValue,
  useLocation,
  useNavigate,
  useRevalidator,
} from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import { useDispatch } from 'react-redux';

// Components
import RouteHeaderCard from '../../components/RouteHeaderCard';
import SegmentList from '../../components/SegmentList';
import MapWithSegments from '../../components/MapWithSegments';
import { Typography } from '../../../../../components/display/Typography';
import { HelpTooltip } from '../../../../../components/display/HelpTooltip';
import { MainButton } from '../../../../../components/inputs/buttons/MainButton';
// El aviso que no bloquea y avisa de una consecuencia: el mismo de corregir un
// tramo, «Registrar parada» y «Transferir tramo».
import {
  CrossingNotice,
  CrossingNoticeIcon,
} from '../../../../../components/modals/submodals/EditSegmentModal/styles';

// Hooks
import { useRouteTimerActions } from '../../../../../hooks/useRouteTimerActions';
import { useSurveillanceRouteGpsTracking } from '../../../../../hooks/useSurveillanceRouteGpsTracking';
import {
  useRoadNamesById,
  useRoadRangesById,
} from '../../../../../hooks/useRoadDirectionLabels';
import {
  useDirectionBlindReason,
  useDirectionOffRouteRoad,
} from '../../../../../state/directionDetection';
import { submitSurveillanceRouteEdit } from '../../../../../gql/services/surveillanceRouteEdit';
import { backendErrorCodes } from '../../../../../gql/retryableBackendCodes';
import {
  showBackendErrorToast,
  showErrorToast,
  showSuccessToast,
} from '../../../../../utils/toasts';
import { useOptimisticRouteState } from '../../../../../hooks/useOptimisticRouteState';
import { usePendingSegments } from '../../../../../hooks/usePendingSegments';
import { useStickyExternalWorks } from '../../../../../hooks/useStickyExternalWorks';
import { useResolvedPromise } from '../../../../../hooks/useResolvedPromise';
import { useAuth } from '../../../../../hooks/useAuth';
import { useOpenLinkedWork } from '../../../../../hooks/useOpenLinkedWork';
import { RV_TRANSFER_REVIEW_ROLES } from '../../../../../utils/rvTransferTargets';
import {
  MOVE_SEGMENTS_ANNULLED_TOAST,
  MOVE_SEGMENTS_DONE_TOAST,
} from '../../../../../utils/moveSegments';
import { useSurveillanceRoutes } from '../../../../../hooks/useSurveillanceRoutes';
import { useChangeSegmentPauseCause } from '../../../../../hooks/useChangeSegmentPauseCause';
import { useSegmentAvisoLink } from '../../../../../hooks/useSegmentAvisoLink';
import { useSegmentReviewState } from '../../../../../hooks/useSegmentReviewState';

// RBAC
import { Role } from '../../../../../types/auth';
import {
  canOperateTimer,
  buildRoidFallback,
  canEditRouteSegments,
  resolveRouteRoadRanges,
  canOpenSegmentEditor,
  segmentEditorMode,
  buildAddRoadToRouteInput,
  buildActionSummaryPath,
  getSurveillanceRouteIdFromPath,
  canChangeSegmentCauseInRoute,
  canLinkOrdersToRoute,
  canLinkBasicActionFromRoute,
  isSegmentPendingSync,
  canReviewRouteSegments,
  computeReviewProgress,
  computeValidateReviewWarning,
  findNextUnreviewedIndex,
  hasOwnReviewMark,
  isSegmentReviewable,
  sortSegmentsByStartDate,
} from '../../../../../utils/surveillanceRoute';
import { computeAutoReviews } from '../../../../../utils/segmentAutoReview';
import { useSegmentReviewAutoStates } from '../../../../../hooks/useSegmentReviewAutoStates';
import {
  endRoadIdOf,
  roadIdsInvolved,
} from '../../../../../utils/roadStretches';

// Modal de corrección de tramo (spec 2026-07-31)
import { openModal } from '../../../../../state/actions/ui/modal';
import { modalIds } from '../../../../../components/modals/SuperModal/types';
import { EditSegmentConfirmPayload } from '../../../../../components/modals/submodals/EditSegmentModal/types';

// Redux
import { roidOTSelected } from '../../../../../state/actions/ui/roidOTSelectedGeneralView';
import { OrderType } from '../../../../../types/orders';
import { isNativeApp } from '../../../../../utils/positionSource';

// Types
import {
  LinkedWorkRef,
  MoveRouteSegmentsInput,
  RouteSegment,
  RouteState,
  SegmentStopInput,
  SegmentTransferTarget,
} from '../../../../../types/surveillanceRoute';
import { RouteDetailData, RoutePreviewLoaderData } from '../../types';
import { EMPTY_HEADER_DATA } from '../headerData';

// Styles
import {
  ContentArea,
  MainContent,
  SectionTitle,
  SectionCard,
  SectionHeaderRow,
  SectionHeaderActions,
  ReviewProgress,
  DirectionBlindRow,
} from '../styles';

/**
 * Referencia estable para «sin tramos»: un `[]` nuevo en cada render invalida
 * los efectos y memos que dependen de los tramos (`usePendingSegments`, los
 * trabajos del aviso…). Mismo patrón que `TimerOverlay`.
 */
const NO_SERVER_SEGMENTS: RouteSegment[] = [];

/**
 * Rechazos de «Deshacer» que dicen que la página no está al día: la parada ya
 * no es la última, ya no existe, alguien corrigió o ligó las piezas, o el
 * recorrido cambió de estado. Ante cualquiera, releer y cerrar.
 */
const UNDO_STALE_CODES: ReadonlySet<string> = new Set([
  'SR_SPLIT_NOT_FOUND',
  'SR_SPLIT_UNDO_NOT_LAST',
  'SR_SPLIT_UNDO_STALE',
  'SR_SPLIT_UNDO_LINKED',
  'SR_SEGMENT_NOT_EDITABLE_STATE',
  'SR_SEGMENT_CHANGED',
]);

// ═══════════════════════════════════════════════════════════════════
// COMPONENT — Task 3 (2026-07-07): recomposición sin panel lateral. La
// cabecera (`RouteHeaderCard`) sube al flujo principal y absorbe estado +
// datos resueltos; el arranque/pausa de timers vive exclusivamente en
// `TimerOverlay` (navbar), esta página ya no los orquesta.
// ═══════════════════════════════════════════════════════════════════

export default function RoutePreviewPage(): JSX.Element {
  const { t } = useTranslation(['pages', 'workOrders', 'modals', 'common']);
  const dispatch = useDispatch();
  // El SNAPSHOT: lo que había en la caché de Apollo, servido al instante por el
  // loader (spec 2026-09-11). Con cobertura degradada es lo único que hay
  // durante un buen rato, y es exactamente lo que el operario estaba viendo.
  const snapshot = useAsyncValue() as RoutePreviewLoaderData;
  // Y lo que trae el servidor, cuando lo trae. `null` = no se pudo refrescar.
  const freshData = useResolvedPromise(snapshot?.fresh);
  // 🔴 El servidor manda en cuanto llega, incluso para decir que el recorrido
  // ya no existe (`route: null`). Lo que NO puede es tumbar la pantalla por no
  // haber llegado: eso es `null` entero, y entonces se queda el snapshot.
  const loaderData = (freshData ?? snapshot) as RoutePreviewLoaderData;
  const userData = useAuth();
  const currentUser = userData?.user?.user;
  const currentRole = currentUser?.rolActive as Role | undefined;

  const [selectedSegment, setSelectedSegment] =
    React.useState<RouteSegment | null>(null);
  const revalidator = useRevalidator();
  const {
    correctSegment,
    updateSegmentFields,
    splitSegment,
    undoSegmentSplit,
    transferSegment,
    moveSegments,
  } = useSurveillanceRoutes();

  const route = loaderData?.route;
  const serverSegments = loaderData?.segments || NO_SERVER_SEGMENTS;
  // Los tramos que esperan en la cola se pintan junto a los del servidor: sin
  // esto, iniciar o cerrar un tramo sin cobertura no se ve ni en la tabla ni en
  // el mapa —la página lee el snapshot del loader, que sin red sirve la caché
  // anterior— y el operario no puede distinguir "no se ve" de "no se guardó".
  // El servidor siempre gana; el pendiente desaparece solo cuando el replay
  // borra la operación de la cola.
  const segments = usePendingSegments(route?.id, serverSegments);
  // Trabajos del aviso: el snapshot no los trae (consulta sin caché), así que
  // en cada revalidación se conserva el último mapa del servidor mientras llega
  // el nuevo; si no, las pills «Resuelto por» parpadean y las filas saltan.
  const externalWorksBySegmentId = useStickyExternalWorks({
    routeId: route?.id,
    segments: serverSegments,
    serverWorks: loaderData?.externalWorksBySegmentId,
    serverArrived: Boolean(freshData),
  });
  // «Tramos revisados» (spec 2026-10-27): marca propia de gestión, con estado
  // optimista local porque el loader no observa la caché de Apollo.
  const canReview = canReviewRouteSegments(currentRole, route);
  const { overlay: reviewOverlay, toggle: toggleReview } =
    useSegmentReviewState(segments);
  // El orden de la TABLA (cronológico): «Siguiente sin revisar» recorre el mismo.
  const orderedSegments = useMemo(
    () => sortSegmentsByStartDate(segments),
    [segments]
  );
  // Revisado automático: el estado de la actuación (de la empresa) da el tramo
  // por revisado sin marca de nadie.
  const autoStates = useSegmentReviewAutoStates();
  const autoReviews = useMemo(
    () => computeAutoReviews(segments, externalWorksBySegmentId, autoStates),
    [segments, externalWorksBySegmentId, autoStates]
  );
  const reviewProgress = useMemo(
    () => computeReviewProgress(segments, reviewOverlay, autoReviews),
    [segments, reviewOverlay, autoReviews]
  );
  const allReviewed =
    reviewProgress.total > 0 && reviewProgress.done === reviewProgress.total;
  const nextUnreviewed = useMemo(() => {
    const index = findNextUnreviewedIndex(
      orderedSegments,
      reviewOverlay,
      autoReviews
    );
    return index >= 0 ? orderedSegments[index] : null;
  }, [orderedSegments, reviewOverlay, autoReviews]);
  // Aviso al validar (D7): solo a quien marca, y lo decide la función.
  const segmentReviewWarning = useMemo(
    () =>
      canReview
        ? computeValidateReviewWarning(segments, reviewOverlay, autoReviews)
        : null,
    [canReview, segments, reviewOverlay, autoReviews]
  );
  const orderRoid = loaderData?.orderRoid;
  const headerData = loaderData?.headerData ?? EMPTY_HEADER_DATA;

  useEffect(() => {
    if (!route || !orderRoid) return;
    dispatch(
      roidOTSelected(
        OrderType.SurveillanceRoute,
        Array.isArray(orderRoid) ? orderRoid.join('.') : orderRoid || '',
        route.id
      )
    );
  }, [route?.id, orderRoid, dispatch]);

  // Timer ownership — only Tablet members of the route's team can create or
  // close timers. The backend enforces the same rule via validateTimerOwnership.
  // Sigue vivo porque gatea el tracking GPS de abajo (no solo botones).
  const canOperate = canOperateTimer(currentUser, route);

  // Timers — Task 3 extrajo `handleOpenStartTimer`/`handleFinishRoute` (+ el
  // `useSurveillanceRoutes()` que envolvían) a `useRouteTimerActions`. El
  // arranque/pausa "en vivo" vive en `TimerOverlay`, PERO el overlay solo
  // lista RVs `in-progress`/`paused` (`GetActiveSurveillanceRoutes`) — un RV
  // `open` nunca aparece ahí, así que el arranque inicial (startRoute +
  // primer createSegment) tiene que seguir viviendo aquí (review final C1).
  // El resume-tras-pausa SÍ está cubierto por el overlay — no se duplica.
  const { handleOpenStartTimer, handleFinishRoute, isLoading } =
    useRouteTimerActions(route, segments);

  // Capture and batch-flush GPS points while the route is in progress.
  // `enabled: canOperate` (bug 2026-07-08): sin este gate, CUALQUIER rol que
  // abriera un RV in-progress en modo lectura (admin/coex/room-team viendo
  // el mapa) disparaba el prompt de geolocalización del navegador — el hook
  // solo miraba `state`, nunca quién está mirando. Mismo gate que los
  // botones de timer (Tablet + mismo equipo del RV): así el dispositivo de
  // la patrulla sigue trackeando igual que antes (arranca al crear el
  // primer tramo, que es lo que pone el RV en in-progress).
  //
  // Task A2/A3 (2026-07-23, plan `rv-mapa-gps-tiempo-real`): el hook ahora
  // expone el estado GPS "vivo" (cursor + traza del tramo activo) — se pasa
  // al mapa para que pinte al instante, sin esperar al flush/refetch.
  // 🔴 El estado que decide si se graba es el EFECTIVO, no el del servidor.
  //
  // El RV pasa a `in-progress` en el BACKEND, al crear el primer tramo. Sin
  // cobertura eso no ocurre: la mutation se queda en la cola, `route.state`
  // sigue diciendo `open` (o `paused` al reanudar tras una pausa) y el tracker
  // no arrancaba. La patrulla conducía creyendo que grababa y no se guardaba ni
  // un punto — y como el servicio nativo de segundo plano se enciende desde
  // este mismo hook, en la app pasaba igual.
  //
  // `useOptimisticRouteState` ya deriva de la cola el estado que el RV VA a
  // tener (`CreateRouteSegment` → in-progress); hasta ahora solo lo consumía la
  // cabecera, así que la pill decía "en curso" con el GPS apagado.
  //
  // Se asume capturar de MÁS si un create encolado acabara descartándose: son
  // unos puntos en IndexedDB que nadie mirará. Capturar de menos pierde el
  // trabajo de una patrulla y no hay forma de recuperarlo.
  const optimisticRouteState = useOptimisticRouteState(route?.id);
  const effectiveRouteState = optimisticRouteState ?? route?.state;

  const { livePosition, liveTrack } = useSurveillanceRouteGpsTracking({
    routeId: route?.id,
    state: effectiveRouteState,
    enabled: canOperate,
  });

  // Tramo activo = el único con `endDate` nulo (mismo criterio que
  // `getSegmentStatus` en MapWithSegments). Acota la traza en vivo/azul al
  // tramo EN CURSO — nunca desde el inicio del recorrido (bug "línea azul
  // persistente" del plan de este task).
  const activeSegment = segments.find((s) => !s.endDate) ?? null;
  const hasActiveSegment = !!activeSegment;

  // Wake Lock (Bloque D, plan `rv-mapa-gps-tiempo-real`, caso 7 "segundo
  // plano"): mantiene la pantalla encendida mientras la patrulla está
  // registrando GPS en un tramo activo — sin esto, muchos navegadores
  // móviles suspenden `watchPosition` al apagarse la pantalla. Mismo gate
  // que el tracker (`canOperate`) + `route.state` explícito (evita pedir el
  // lock durante un `open` sin tramo, aunque `hasActiveSegment` ya lo cubre
  // por construcción — un RV `open` nunca tiene tramos). El banner de abajo
  // avisa siempre que hay tramo activo, independientemente de si el
  // navegador concedió el lock (`isLocked` no cambia el mensaje — el GPS
  // sigue capturando igual sin él, ver JSDoc de `useWakeLock`).
  // El Wake Lock ya NO se pide aquí: lo hace `TimerOverlay` (montado en el
  // Header, presente en toda la app) para que sobreviva a que el operario
  // navegue a otra vista con el tramo abierto. Pedirlo también aquí abriría un
  // segundo sentinel para la misma condición. El aviso de abajo se queda: es
  // información de esta pantalla.

  // Precache de la malla del recorrido (Bloque B, Task B2): puebla la caché
  // offline (`getCachedRoadMesh`) que `useGpsToPK` usa como fallback cuando
  // `pkByCoordinates` falla sin red — así el PK al finalizar (B3) puede
  // resolverse aunque el operario esté sin cobertura en ese instante. El
  // sentido usa el del tramo ACTIVO (mismo criterio que el mapa arriba, ver
  // su comentario) — `SurveillanceRoute` no tiene `direction` propio.
  // Multi-carretera (2026-08-15): se precachean las mallas de TODAS las
  // carreteras del recorrido, o el PK offline solo funcionaría en la primera —
  // que es justo el caso que rompe cuando la patrulla entra en el ramal.
  // Nombres de las carreteras del recorrido: la tabla de tramos pinta la
  // columna «Carretera» solo si hay más de una.
  // Las del recorrido MÁS las de los tramos — ver `roadIdsInvolved`: no son el
  // mismo conjunto en cuanto alguien quita una carretera del recorrido.
  const roadIdsParaNombres = useMemo(
    () => roadIdsInvolved(route?.roadIDs, segments),
    [route?.roadIDs, segments]
  );
  const roadNamesById = useRoadNamesById(roadIdsParaNombres);
  const roadRangesById = useRoadRangesById(route?.roadIDs);

  /**
   * Qué trozo de cada carretera dibuja el mapa de fondo.
   *
   * Tres escalones: el rango que el recorrido declara para esa carretera; si no
   * lo hay y es la PRIMERA, los PK del propio recorrido (que son los suyos por
   * definición); y si no, los extremos declarados de la vía. Sin el tercero, un
   * ramal sin rango propio se filtraría por el kilometraje de la troncal y
   * saldría un trozo arbitrario — sus PK no son comparables.
   */
  const roadWindows = useMemo(
    () => resolveRouteRoadRanges(route, roadRangesById),
    [route, roadRangesById]
  );

  // La malla ya no se precachea aquí: se descarga al arrancar la app
  // (`ensureRoadMesh`) y vive en `roadMeshStore`. Así el PK sin cobertura
  // funciona aunque nunca se haya abierto esta pantalla — que es donde se
  // abren los modales del cronómetro desde el `TimerOverlay`.

  // Detección del cambio de sentido (spec 2026-08-14) — sustituye al botón
  // manual que había en el `TimerOverlay`. Se alimenta de la MISMA traza que
  // pinta el mapa, así que no cuesta ni una lectura de GPS más, y vive aquí
  // porque aquí es donde corre el tracking: donde no hay captura tampoco
  // puede haber detección, y anunciar el aviso en otra pantalla sería
  // prometer algo que no se puede cumplir.
  // El detector de cambio de sentido ya NO vive aquí: se mudó al
  // `TimerOverlay`, o sea a toda la app, por el mismo motivo que el guardián
  // de la captura. Atado a esta pantalla, salir de ella dejaba el giro sin
  // detectar, y con la pantalla apagada el corte caía cientos de metros atrás
  // (spec 2026-09-01). Aquí solo se LEE su aviso de ceguera, que es lo único
  // suyo que se pinta en esta pantalla.
  const directionBlindReason = useDirectionBlindReason();

  // ── Vía de APOYO (2026-09-23) ─────────────────────────────────────────
  //
  // Con el aviso «fuera del trazado», la vía de su concesión que pisa la
  // patrulla —si es una—. El aviso la nombra y ofrece añadirla al recorrido:
  // desde ese momento es del recorrido, el detector la sigue y el cronómetro
  // la ofrece. Sin cobertura la edición se encola y surte efecto igual
  // (`submitSurveillanceRouteEdit` la escribe en la caché).
  const offRouteRoadId = useDirectionOffRouteRoad();
  const offRouteRoadNames = useRoadNamesById(
    offRouteRoadId ? [offRouteRoadId] : []
  );
  const apolloClient = useApolloClient();
  const [isAddingRoad, setIsAddingRoad] = useState(false);
  const handleAddSupportRoad = useCallback(async () => {
    if (!route?.id || !offRouteRoadId || isAddingRoad) return;
    setIsAddingRoad(true);
    try {
      const outcome = await submitSurveillanceRouteEdit(
        apolloClient,
        buildAddRoadToRouteInput(route, offRouteRoadId, crypto.randomUUID())
      );
      showSuccessToast(
        outcome === 'queued'
          ? t('modals:changeState.queuedOffline')
          : t('toasts:success.updated')
      );
      // La pantalla sirve el recorrido del loader: que lo vuelva a leer (de
      // la red o, sin cobertura, de la caché ya escrita).
      revalidator.revalidate();
    } catch (err) {
      showBackendErrorToast(err, t('toasts:error.generic'));
    } finally {
      setIsAddingRoad(false);
    }
  }, [route, offRouteRoadId, isAddingRoad, apolloClient, revalidator, t]);

  // ═══════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════

  // Bidireccional tabla↔mapa (2026-07-08): mismo handler para el click de
  // fila (SegmentList) y el click de dot de pausa (MapWithSegments).
  // Clicar el MISMO tram ya seleccionado deselecciona (toggle) — vuelve
  // todo el mapa a su estado normal en vez de quedar "atascado" resaltado.
  const handleSegmentClick = useCallback((segment: RouteSegment) => {
    setSelectedSegment((prev) => (prev?.id === segment.id ? null : segment));
  }, []);

  // «Siguiente sin revisar» (spec 2026-10-27 § 9): lo mismo que un toque en la
  // fila —la selecciona y el mapa la resalta— pero SIN alternar: pulsarlo con
  // ese tramo ya seleccionado no lo deselecciona. Y baja hasta la fila aunque
  // ya estuviera seleccionada (el usuario puede haberse ido a otra parte).
  const [reviewScrollRequest, setReviewScrollRequest] = useState<{
    segmentId: string;
    seq: number;
  } | null>(null);
  const handleNextUnreviewed = useCallback(() => {
    if (!nextUnreviewed) return;
    setSelectedSegment(nextUnreviewed);
    setReviewScrollRequest((prev) => ({
      segmentId: nextUnreviewed.id,
      seq: (prev?.seq ?? 0) + 1,
    }));
  }, [nextUnreviewed]);

  // Casilla de cabecera con todos marcados: desmarcarlos se confirma antes (se
  // pierde el progreso de golpe). Van los que llevan MI marca.
  const handleRequestUnreviewAll = useCallback(() => {
    const markedIds = orderedSegments
      .filter(
        (segment) =>
          isSegmentReviewable(segment) &&
          hasOwnReviewMark(segment, reviewOverlay)
      )
      .map((segment) => segment.id);
    if (markedIds.length === 0) return;
    dispatch(
      openModal(modalIds.UNREVIEW_ALL_SEGMENTS, {
        onConfirm: () => toggleReview(markedIds, false),
      })
    );
  }, [dispatch, orderedSegments, reviewOverlay, toggleReview]);

  // Corrección de tramos (spec 2026-07-31) — herramienta de GESTIÓN: el
  // operario no corrige, corrige quien revisa. Con el trabajo en curso no se
  // permite (hay cronómetro vivo) y en terminales hay que reabrir antes; de
  // ahí que solo valgan `paused` y `pending-validation`.
  // El permiso vive en `canEditRouteSegments` porque hay DOS puntos de entrada
  // al mismo modal (aquí y la card del tramo en "Órdenes asociadas").
  const editorMode = useMemo(
    () => segmentEditorMode(currentUser, route),
    [currentUser, route]
  );
  const canEditSegments = useMemo(
    () => canOpenSegmentEditor(currentUser, route),
    [currentUser, route]
  );

  const handleEditSegment = useCallback(
    (segment: RouteSegment) => {
      // Si el tramo lleva MI marca (viva o en ámbar), tras corregirlo se
      // reenvía: que mi propia corrección no me lo deje en ámbar (spec § 9).
      const reconfirmReview =
        canReview && hasOwnReviewMark(segment, reviewOverlay);
      dispatch(
        openModal(modalIds.EDIT_SEGMENT, {
          segment,
          segments,
          // Corregir un tramo se valida contra la vía donde ACABÓ, que con
          // trozos no es la de salida.
          roadId: endRoadIdOf(segment) ?? route?.roadIDs?.[0],
          // mc53: las del recorrido, para poder corregir la del tramo a mano.
          roadIds: route?.roadIDs ?? [],
          mode: editorMode ?? 'management',
          onConfirm: async (payload: EditSegmentConfirmPayload) => {
            if (editorMode === 'operator') {
              // El operario solo manda lo suyo. El payload trae PK y horas sin
              // tocar (el modal se los enseña bloqueados), pero no viajan: su
              // mutation ni siquiera tiene dónde ponerlos.
              await updateSegmentFields({
                id: payload.id,
                direction: payload.direction,
                lane: payload.lane,
                pauseCauseDetail: payload.pauseCauseDetail ?? '',
              });
            } else {
              await correctSegment(payload, route?.orderIDs?.[0]);
            }
            // Guardado o encolado sin red: las dos resuelven (un error lanza y
            // el modal sigue abierto). Encolada, la marca va DETRÁS de la
            // corrección en la cola, así que el servidor la ve después.
            if (reconfirmReview) toggleReview([segment.id], true);
            // Las páginas del RV leen el SNAPSHOT del loader: sin revalidar,
            // la tabla seguiría mostrando los PKs viejos aunque la caché de
            // Apollo ya tenga los nuevos.
            revalidator.revalidate();
          },
        })
      );
    },
    [
      canReview,
      correctSegment,
      dispatch,
      editorMode,
      reviewOverlay,
      revalidator,
      route?.orderIDs,
      route?.roadIDs,
      segments,
      toggleReview,
      updateSegmentFields,
    ]
  );

  // «Registrar parada» (spec 2026-10-06): herramienta de GESTIÓN hermana de
  // «Corregir» — mismas reglas de acceso, sin el modo operario.
  const canRegisterStops = canEditSegments && editorMode === 'management';

  // ¿Se puede crear la básica en este recorrido? Decide si «Actuación» se
  // ofrece como causa (cambiar causa, causa de la parada) y si sale «Vincular
  // a actuación» (15-oct): la misma regla que la pastilla de la card.
  const canLinkAction = canLinkBasicActionFromRoute(currentUser, route);

  const handleRegisterStop = useCallback(
    (segment: RouteSegment) => {
      dispatch(
        openModal(modalIds.REGISTER_SEGMENT_STOP, {
          segment,
          segments,
          roadId: endRoadIdOf(segment) ?? route?.roadIDs?.[0],
          roadIds: route?.roadIDs ?? [],
          canChooseActionCause: canLinkAction,
          onConfirm: async (stops: SegmentStopInput[]) => {
            const result = await splitSegment(
              segment.id,
              stops,
              route?.orderIDs?.[0]
            );
            if (!result) return;
            // Qué pasó con el aviso: el usuario pidió que se vincule solo, así
            // que el toast dice a qué OT, o por qué no.
            const linked = result.links.filter((l) => l.reason === 'linked');
            const message = linked.length
              ? t('surveillance.registerStop.toastLinked', {
                  roid: linked.map((l) => l.order?.roid).join(', '),
                })
              : result.links.some((l) => l.reason === 'ambiguous')
                ? t('surveillance.registerStop.toastAmbiguous')
                : result.links.some((l) => l.reason === 'no-candidate')
                  ? t('surveillance.registerStop.toastNoCandidate')
                  : t('surveillance.registerStop.toastPending');
            showSuccessToast(message);
            // Las páginas del RV leen el SNAPSHOT del loader (ver «Corregir»).
            revalidator.revalidate();
          },
        })
      );
    },
    [
      canLinkAction,
      dispatch,
      revalidator,
      route?.orderIDs,
      route?.roadIDs,
      segments,
      splitSegment,
      t,
    ]
  );

  const handleUndoStop = useCallback(
    (segment: RouteSegment) => {
      dispatch(
        openModal(modalIds.UNDO_SEGMENT_STOP, {
          onConfirm: async () => {
            try {
              await undoSegmentSplit(segment.id, route?.orderIDs?.[0]);
            } catch (err) {
              // QA 15-oct (T1.7+a): con la página vieja (otra persona registró
              // o deshizo una parada después) la tabla ofrecía «Deshacer» y el
              // servidor ya no lo deja. El aviso ya lo puso el hook con su
              // código; aquí se relee la página para enseñar cómo está y el
              // modal se cierra. Un fallo de red o del servidor, no: el modal
              // sigue abierto para repetirlo.
              if (
                backendErrorCodes(err).some((code) =>
                  UNDO_STALE_CODES.has(code)
                )
              ) {
                revalidator.revalidate();
                return;
              }
              throw err;
            }
            showSuccessToast(t('surveillance.registerStop.toastUndone'));
            revalidator.revalidate();
          },
        })
      );
    },
    [dispatch, revalidator, route?.orderIDs, t, undoSegmentSplit]
  );

  // «Transferir tramo» (spec 2026-10-07): SuperAdmin, Coex y Capataz (Sala
  // no transfiere actuaciones ni tramos) y con el recorrido pendiente de
  // validar (el servidor exige lo mismo en el destino).
  const canTransferSegments =
    canRegisterStops &&
    route?.state === RouteState.PENDING_VALIDATION &&
    RV_TRANSFER_REVIEW_ROLES.includes(currentRole ?? '');

  // D3 (2026-07-23): abrir las Orders vinculadas desde la tabla de Tramos, en
  // la misma pestaña y con las mismas rutas que la card de «Órdenes
  // asociadas». Vivía dentro de la tabla; desde el 14-oct la tabla no usa el
  // router (también se pinta en el modal de «Registrar parada»).
  const navigate = useNavigate();
  const location = useLocation();
  const openBasicAction = useCallback(
    (actionId: string) => {
      // La actuación se abre DENTRO del recorrido (2026-08-15).
      navigate(
        buildActionSummaryPath(
          actionId,
          getSurveillanceRouteIdFromPath(location.pathname)
        )
      );
    },
    [location.pathname, navigate]
  );
  const openLinkedWork = useOpenLinkedWork();
  const openAvisoWork = useCallback(
    (work: LinkedWorkRef, segment: RouteSegment) =>
      openLinkedWork(
        work,
        segment.linkedExternalOrder ?? {},
        getSurveillanceRouteIdFromPath(location.pathname)
      ),
    [location.pathname, openLinkedWork]
  );
  const openExternalOrder = useCallback(
    (orderId: string, type?: string | null) => {
      navigate(
        type === OrderType.Campaign
          ? `/work-orders/campaign/${orderId}`
          : `/work-orders/${orderId}`
      );
    },
    [navigate]
  );

  const handleTransferSegment = useCallback(
    (segment: RouteSegment) => {
      const ordered = [...segments].sort(
        (a, b) =>
          new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
      );
      dispatch(
        openModal(modalIds.TRANSFER_ROUTE_SEGMENT, {
          segment,
          segmentNumber: ordered.findIndex((s) => s.id === segment.id) + 1,
          routeSegmentsCount: segments.length,
          onConfirm: async (
            target: SegmentTransferTarget,
            clientOpId: string
          ) => {
            const result = await transferSegment(
              segment.id,
              target.routeId,
              clientOpId,
              // El recorrido que el usuario está viendo (QA 15-oct, T2.10).
              route?.id
            );
            if (!result) return;
            showSuccessToast(
              t('surveillance.transferSegment.toastDone', {
                route: target.roid,
                roid: result.roid,
              })
            );
            // Una básica que se ha quedado a 0 min hay que editarla a mano:
            // el aviso no se cierra solo para que no pase desapercibido.
            const zeroLength = result.zeroLengthBasics ?? [];
            if (zeroLength.length > 0) {
              showErrorToast(
                t('surveillance.transferSegment.toastZeroBasic', {
                  roids: zeroLength
                    .map((basic) => basic.roid ?? basic.id)
                    .join(', '),
                }),
                { autoClose: false }
              );
            }
            // El tramo ya no es de este recorrido: la tabla tiene que releerse.
            revalidator.revalidate();
          },
        })
      );
    },
    [dispatch, revalidator, route?.id, segments, t, transferSegment]
  );

  // Padre de la Actuación básica que se crea desde un tramo (causa que pasa a
  // «Actuación» o «Vincular a actuación»): la Order gemela del recorrido, como
  // en el cierre de tramo del cronómetro.
  const routeParentOrder = useMemo(
    () =>
      route
        ? {
            id: route.orderIDs?.[0],
            roid:
              (Array.isArray(orderRoid) ? orderRoid.join('.') : orderRoid) ||
              buildRoidFallback(route),
          }
        : null,
    [orderRoid, route],
  );

  // Cambiar la causa desde la tabla (2026-10-07): mismo modal y misma lógica
  // que la etiqueta «Causa: X» de «Órdenes asociadas» (hook compartido).
  // Gestión y con el recorrido sin cerrar (revisión I1, 15-oct); qué filas
  // admiten el cambio lo decide la tabla.
  const canChangeCause = canChangeSegmentCauseInRoute(
    currentRole,
    route?.state,
  );
  // Ids de tramos cuya causa se abrió para cambiar llevando MI marca (se lee
  // al abrir, por id): `onSaved` trae el id del cambio guardado y decide con
  // ESE id, aunque entretanto se haya abierto el modal de otro tramo.
  const causeReconfirmIdsRef = useRef<Set<string>>(new Set());
  const handleCauseSaved = useCallback(
    (segmentId: string) => {
      // Guardado o encolado (el hook solo llama aquí si no hubo error): se
      // reenvía la marca, como tras «Corregir».
      if (causeReconfirmIdsRef.current.delete(segmentId)) {
        toggleReview([segmentId], true);
      }
      // Las páginas del RV leen el SNAPSHOT del loader: sin revalidar, la
      // tabla seguiría enseñando la causa vieja.
      revalidator.revalidate();
    },
    [revalidator, toggleReview]
  );
  const { openChangeCause, launchBasicActionChain } =
    useChangeSegmentPauseCause(handleCauseSaved);
  const handleChangeCause = useCallback(
    (segment: RouteSegment) => {
      if (!route || !segment.pauseCause) return;
      if (canReview && hasOwnReviewMark(segment, reviewOverlay)) {
        causeReconfirmIdsRef.current.add(segment.id);
      } else {
        causeReconfirmIdsRef.current.delete(segment.id);
      }
      openChangeCause({
        segmentId: segment.id,
        routeId: route.id,
        parentOrder: routeParentOrder,
        currentCause: segment.pauseCause,
        currentDetail: segment.pauseCauseDetail,
        // El modal avisa de que el cambio suelta lo vinculado: la básica o la
        // OT del aviso, igual que la card (que resuelve cualquiera de las dos).
        linkedOrderRoid:
          segment.linkedBasicActionOrder?.roid ??
          segment.linkedExternalOrder?.roid,
        canChooseAction: canLinkAction,
      });
    },
    [
      canLinkAction,
      canReview,
      openChangeCause,
      reviewOverlay,
      route,
      routeParentOrder,
    ],
  );

  // Columna «Vincular» (2026-10-08): lo que la card del tramo deja hacer con
  // sus vínculos, con SUS permisos y SUS hooks. Ver y navegar ya lo hacen los
  // enlaces de «Act. básica» y «OT sala».
  const canLinkAviso = canLinkOrdersToRoute(currentUser, route);
  const { linkAviso, changeAviso, unlinkAviso, unlinkingSegmentId } =
    useSegmentAvisoLink(revalidator.revalidate);
  const handleLinkAviso = useCallback(
    (segment: RouteSegment) => linkAviso(segment.id, segment.endDate),
    [linkAviso],
  );
  const handleChangeAviso = useCallback(
    (segment: RouteSegment) => {
      const current = segment.linkedExternalOrder?.id;
      if (current) changeAviso(segment.id, current, segment.endDate);
    },
    [changeAviso],
  );
  const handleUnlinkAviso = useCallback(
    (segment: RouteSegment) => {
      const current = segment.linkedExternalOrder?.id;
      // `unlinkAviso` no rechaza: captura su error (el toast ya lo avisa).
      if (current) unlinkAviso(segment.id, current);
    },
    [unlinkAviso],
  );
  const handleLinkAction = useCallback(
    (segment: RouteSegment) => {
      if (!route) return;
      launchBasicActionChain(
        { segmentId: segment.id, routeId: route.id, parentOrder: routeParentOrder },
        segment.pauseCauseDetail,
      );
    },
    [launchBasicActionChain, route, routeParentOrder],
  );
  // «Mover tramos» (plan 2026-10-15 § 3): los mismos roles y estado que
  // «Transferir» (SuperAdmin, Coex, Capataz; recorrido pendiente de validar).
  // Tras mover, la página se queda en el origen y relee su tabla; si el
  // origen se anuló (juntar), va al destino, que es donde está el turno.
  const handleMoveSegments = useCallback(() => {
    if (!route) return;
    dispatch(
      openModal(modalIds.MOVE_ROUTE_SEGMENTS, {
        route,
        routeRoid:
          (Array.isArray(orderRoid) ? orderRoid.join('.') : orderRoid) ||
          buildRoidFallback(route),
        // Un tramo aún en la cola no existe en el servidor: no se mueve.
        segments: segments.filter((s) => !isSegmentPendingSync(s)),
        onConfirm: async (input: MoveRouteSegmentsInput) => {
          const result = await moveSegments(input);
          if (!result) return;
          showSuccessToast(
            t('surveillance.moveSegments.toastDone', {
              count: result.movedCount,
              route: result.targetRoid,
            }),
            { toastId: MOVE_SEGMENTS_DONE_TOAST }
          );
          const zeroLength = result.zeroLengthBasics ?? [];
          if (zeroLength.length > 0) {
            showErrorToast(
              t('surveillance.transferSegment.toastZeroBasic', {
                roids: zeroLength
                  .map((basic) => basic.roid ?? basic.id)
                  .join(', '),
              }),
              { autoClose: false }
            );
          }
          if (result.sourceAnnulled) {
            showSuccessToast(
              t('surveillance.moveSegments.toastAnnulled', {
                route: result.sourceRoid,
              }),
              { toastId: MOVE_SEGMENTS_ANNULLED_TOAST }
            );
            navigate(`/surveillance-route/${result.targetRouteId}`);
            return;
          }
          revalidator.revalidate();
        },
      })
    );
  }, [
    dispatch,
    moveSegments,
    navigate,
    orderRoid,
    revalidator,
    route,
    segments,
    t,
  ]);

  if (!route) {
    return <div>{t('surveillance.routeNotFound')}</div>;
  }

  const resolvedRoid =
    (Array.isArray(orderRoid) ? orderRoid.join('.') : orderRoid) ||
    buildRoidFallback(route);

  return (
    <ContentArea>
      <MainContent>
        <RouteHeaderCard
          route={route}
          roid={resolvedRoid}
          headerData={headerData}
          onFinishRoute={handleFinishRoute}
          onStartRoute={handleOpenStartTimer}
          isLoading={isLoading}
          segmentReviewWarning={segmentReviewWarning}
          onReviewRemaining={handleNextUnreviewed}
        />

        {/* Prevención P4 (plan 2026-10-15 § 5): con más de 24 h entre el primer
            y el último tramo, Gestión Horaria NO crea las horas del turno
            (techo de `reconcileForRoute`) y hasta ahora lo hacía en silencio
            (Su.3898: dos noches dentro, cero horas). Lo calcula el servidor. */}
        {route.shiftWarning === 'span-over-24h' && (
          <CrossingNotice role="alert" data-testid="route-span-over-24h-notice">
            <CrossingNoticeIcon aria-hidden="true">⚠</CrossingNoticeIcon>
            <span>{t('pages:surveillance.shiftWarning.spanOver24hNotice')}</span>
          </CrossingNotice>
        )}

        {/* Aviso Wake Lock (Bloque D, caso 7 "segundo plano") — mismo patrón
            que el aviso `roadChangeSegmentsWarning` de `UbicacioTab`
            (Typography discreto S/greyDark, sin styled nuevo). Solo mientras
            hay tramo activo Y el operario puede operar el timer — para
            lectura (admin/coex/room-team) el GPS ni siquiera se captura.

            En la APP NATIVA no se muestra, y no es cosmético: allí sería
            FALSO. La condición de verdad no es "hay tramo" sino "la captura
            depende de que la pantalla siga viva", y eso solo pasa en web —
            en la app la mantiene un foreground service que sigue grabando
            con la pantalla apagada y la app cerrada. Dejarlo diría al
            operario que no guarde la tablet, justo lo contrario de lo que la
            app viene a permitir. */}
        {hasActiveSegment && canOperate && !isNativeApp() && (
          <Typography
            component="p"
            variant="regular"
            size="S"
            colorText="greyDark"
          >
            {t('surveillance.wakeLockNotice')}
          </Typography>
        )}

        {/* Detector ciego: se dice el MOTIVO, no un genérico. Sin esto el
            operario no puede distinguir "no he girado" de "el sistema no se
            está enterando de que giro". */}
        {hasActiveSegment && canOperate && directionBlindReason && (
          <DirectionBlindRow>
            <Typography
              component="p"
              variant="regular"
              size="S"
              colorText="red"
              data-testid="direction-detector-blind"
            >
              {t('pages:surveillance.directionDetectorBlind', {
                reason:
                  directionBlindReason === 'off-road' && offRouteRoadId
                    ? t('pages:surveillance.directionDetectorOffRouteRoad', {
                        road:
                          offRouteRoadNames[offRouteRoadId] || offRouteRoadId,
                      })
                    : t(
                        directionBlindReason === 'off-road'
                          ? 'pages:surveillance.directionDetectorOffRoad'
                          : 'pages:surveillance.directionDetectorNoMesh'
                      ),
              })}
            </Typography>
            {directionBlindReason === 'off-road' && offRouteRoadId && (
              <MainButton
                variant="secondary"
                text={t('common:buttons.add')}
                disabled={isAddingRoad}
                onClick={handleAddSupportRoad}
                data-testid="direction-detector-add-road"
              />
            )}
          </DirectionBlindRow>
        )}

        {/* Mapa */}
        <MapWithSegments
          // Pantalla completa (2026-09-01): solo aquí. Es la pantalla donde se
          // LEE el recorrido entero y donde el alto fijo del mapa se queda
          // corto; el mapa del asistente se queda como estaba.
          allowFullscreen
          segments={segments}
          gpsPoints={route?.gpsPoints || []}
          roadId={route?.roadIDs?.[0]}
          roadIds={route?.roadIDs}
          roadWindows={roadWindows}
          pkInitKm={route?.pkInitKm}
          pkInitMeter={route?.pkInitMeter}
          pkEndKm={route?.pkEndKm}
          pkEndMeter={route?.pkEndMeter}
          // Bug "Caso 6" (mapa gris sin flecha): `direction="top"` estaba
          // HARDCODEADO — para un RV patrullado en sentido `down` la query
          // de fallback (`GET_POSITION_ROADS`, exact-match) no devolvía
          // nada. `SurveillanceRoute` no tiene un `direction` propio (solo
          // lo tiene `RouteSegment` — ver `SURVEILLANCE_ROUTE_CARD_FRAGMENT`
          // en gql/fragments/surveillanceRoute.ts, que documenta que ese
          // campo NO existe en el tipo de la ruta), así que el sentido real
          // más fiable disponible es el del tramo ACTIVO actual.
          direction={activeSegment?.direction ?? 'top'}
          livePosition={livePosition}
          liveTrack={liveTrack}
          activeSegmentStartDate={activeSegment?.startDate ?? null}
          onSegmentClick={handleSegmentClick}
          selectedSegmentId={selectedSegment?.id}
        />

        <SectionCard>
          <SectionHeaderRow>
            <SectionTitle>
              {t('surveillance.segments')}
              {/* Para qué es la casilla de la primera columna (2-nov): la
                  cabecera solo lleva la casilla para que la columna sea
                  estrecha. En tablet la burbuja sale al tocar la «i». */}
              {canReview && (
                <HelpTooltip text={t('surveillance.segmentReview.help')} />
              )}
            </SectionTitle>
            {(canReview || canTransferSegments) && (
              <SectionHeaderActions>
                {/* «Tramos revisados» (spec 2026-10-27 § 9): progreso y
                    «Siguiente sin revisar», solo para quien marca. */}
                {canReview && (
                  <>
                    <ReviewProgress
                      size="M"
                      variant="semiBold"
                      colorText={allReviewed ? 'darkForestGreen' : 'black'}
                      role="status"
                      data-testid="segment-review-progress"
                    >
                      {t('surveillance.segmentReview.progress', {
                        done: reviewProgress.done,
                        total: reviewProgress.total,
                      })}
                    </ReviewProgress>
                    <MainButton
                      variant="secondary"
                      text={t(
                        allReviewed
                          ? 'surveillance.segmentReview.allDone'
                          : 'surveillance.segmentReview.next'
                      )}
                      onClick={handleNextUnreviewed}
                      disabled={!nextUnreviewed}
                      data-testid="segment-review-next"
                    />
                  </>
                )}
                {canTransferSegments && (
                  <MainButton
                    variant="secondary"
                    text={t('surveillance.moveSegments.open')}
                    onClick={handleMoveSegments}
                    data-testid="move-segments-open"
                  />
                )}
              </SectionHeaderActions>
            )}
          </SectionHeaderRow>
          <SegmentList
            segments={segments}
            onSegmentClick={handleSegmentClick}
            selectedSegmentId={selectedSegment?.id}
            canEditSegments={canEditSegments}
            onEditSegment={handleEditSegment}
            canRegisterStops={canRegisterStops}
            onRegisterStop={handleRegisterStop}
            onUndoStop={handleUndoStop}
            canTransferSegments={canTransferSegments}
            onTransferSegment={handleTransferSegment}
            canChangeCause={canChangeCause}
            onChangeCause={handleChangeCause}
            canLinkAviso={canLinkAviso}
            canLinkAction={canLinkAction}
            onLinkAviso={handleLinkAviso}
            onChangeAviso={handleChangeAviso}
            onUnlinkAviso={handleUnlinkAviso}
            onLinkAction={handleLinkAction}
            busyAvisoSegmentId={unlinkingSegmentId}
            roadNamesById={roadNamesById}
            onOpenBasicAction={openBasicAction}
            onOpenExternalOrder={openExternalOrder}
            externalWorksBySegmentId={externalWorksBySegmentId}
            onOpenLinkedWork={openAvisoWork}
            canReview={canReview}
            reviewOverlay={reviewOverlay}
            autoReviews={autoReviews}
            onToggleReview={toggleReview}
            onRequestUnreviewAll={handleRequestUnreviewAll}
            scrollRequest={reviewScrollRequest}
          />
        </SectionCard>

        {/*
          "Crear incidència" ELIMINADO (2026-07-17). Era un vestigio del
          mockup que el propio V4 ya daba por retirado ("Cerradas — iteración
          1", punto 7: «Eliminado; flujo cubierto por `Finalitzar tram →
          causa action`»), y FR-9.1 solo reconoce dos invocaciones del wizard
          P9: el botón `Crear Actuació` (P8 / tab Órdenes asociadas) y el
          `StopCauseModal` con causa `action`. Abría exactamente el mismo
          wizard que `Crear Actuació` pero SIN resolver capitol/subcapitol
          (el "BUG #19 FIX" de ShowOT), así que era el peor de los dos
          caminos. El resume tras F5 (I2) no dependía de este botón: vive en
          el saveData del wizard y se dispara por la RUTA
          (`consumeFromSegment`), así que sobrevive entrando por `Crear
          Actuació`.
        */}
      </MainContent>
    </ContentArea>
  );
}
